import { API_BASE_URL, ApiError } from './client';
import { ComplianceRefusal } from './conversations';
import { ValidationFailure } from './events';
import type { FieldErrors } from './events';
import { downsizeImage } from '../lib/imageDownsize';

export interface Announcement {
  id: string;
  title: string;
  body: string;
  authorPersonId: string;
  authorDisplayName: string;
  /** Their position in this unit, as of now. Empty when they no longer hold one. */
  authorRole: string;
  isPinned: boolean;
  /** An instant. Rendered relative — an announcement is state, so the exact minute rarely matters. */
  publishedAt: string;
  conversationId: string | null;
  /**
   * Messages in this announcement's thread I have not been shown. Zero for a thread I cannot read
   * at all — an announcement's thread can be adults-only, and a count would itself be a disclosure.
   */
  unreadCount: number;
  /**
   * What the announcement hands out, in the order its author put them — TROOP-255. Live ones only:
   * a document whose file was taken down is simply absent. Readers are the announcement's readers,
   * so there is no per-attachment audience to check here.
   */
  attachments: AnnouncementAttachment[];
  /**
   * The announcement itself is new **to me** — published in the last fortnight and not yet shown
   * to me (TROOP-268). A different fact from `unreadCount`, which is replies: the card says *New*
   * for this and carries the replies on its Discussion line.
   */
  isNew: boolean;
}

/**
 * One handout on an announcement: an uploaded document, or a link somebody typed into its own
 * fields. Never parsed out of the body, which draws its own links through `CuratedText`.
 */
export interface AnnouncementAttachment {
  id: string;
  kind: 'Document' | 'Link';
  label: string;
  /** Link only (null for a document): the https address to open. */
  linkUrl: string | null;
  /**
   * Link only: the full hostname, punycode for an international name. Rendered visibly beside the
   * label and never shortened — that is the whole mitigation, the same one a requirement's
   * external link carries.
   */
  linkHost: string | null;
  /** Document only (null for a link). The URL is signed and short-lived. */
  file: {
    storedFileId: string;
    url: string;
    contentType: string;
    byteSize: number;
    expiresAt: string;
  } | null;
}

/** What the publish form sends per attachment. The array's order is the display order. */
export type AttachmentInput =
  | { kind: 'Document'; storedFileId: string; label: string }
  | { kind: 'Link'; label: string; url: string };

export interface PublishAnnouncementInput {
  title: string;
  body: string;
  isPinned: boolean;
  threadAudience?: 'Everyone' | 'AdultsOnly';
  attachments?: AttachmentInput[];
}

/** A document uploaded ahead of publishing, waiting to be named in the publish payload. */
export interface UploadedDocument {
  storedFileId: string;
  contentType: string;
  byteSize: number;
}

export interface PublishedAnnouncement {
  announcementId: string;
  conversationId: string;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });

  if (!response.ok) {
    let problem: { title?: string; detail?: string; rule?: string; errors?: FieldErrors } = {};
    try {
      problem = await response.json();
    } catch {
      // Non-JSON body; the status is enough.
    }

    // 400 carries per-field messages, so the form can put each one under its own box.
    if (response.status === 400 && problem.errors) {
      throw new ValidationFailure(problem.errors);
    }

    if (response.status === 409 && problem.title) {
      throw new ComplianceRefusal(problem.title, problem.detail ?? '', problem.rule);
    }

    throw new ApiError(
      response.status,
      problem.detail ?? problem.title ?? `Failed (${response.status}).`,
    );
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

/**
 * One announcement on a parent's list. Carries its unit, because their list spans every unit their
 * children are in and "dues are payable Friday" is a different instruction depending on which
 * troop said it.
 */
export interface FamilyAnnouncement extends Announcement {
  unitId: string;
  unitName: string;
}

/**
 * A bounded noticeboard, and how big it really is.
 *
 * `rows` is the most recent handful, pinned first; `total` is everything the unit has published.
 * The screen draws fewer than it is handed and heads the section with `total`, which is the trade
 * `ParticipantStrip` makes with its `+N` pill -- a bound that says so, rather than a list that
 * quietly stops. It was neither before: 268 rows came back and 268 were drawn (TROOP-96).
 */
export interface AnnouncementList<T> {
  rows: T[];
  total: number;
}

/** The same noticeboard, through the door a guardian can actually reach (TROOP-69). */
export const familyAnnouncementsApi = {
  list: () => call<AnnouncementList<FamilyAnnouncement>>('/family/announcements/'),

  detail: (announcementId: string) =>
    call<FamilyAnnouncement>(`/family/announcements/${announcementId}`),
};

export const announcementsApi = {
  /** Pinned first, then newest. The order is decided server-side so no screen can differ. */
  list: (unitId: string) => call<AnnouncementList<Announcement>>(`/units/${unitId}/announcements/`),

  detail: (unitId: string, announcementId: string) =>
    call<Announcement>(`/units/${unitId}/announcements/${announcementId}`),

  publish: (unitId: string, input: PublishAnnouncementInput) =>
    call<PublishedAnnouncement>(`/units/${unitId}/announcements/`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /**
   * Uploads a document for an announcement that has not been published yet — the PDF or picture
   * goes up the moment it is picked, and the returned id rides the publish payload. An uploaded
   * file nobody publishes is harmless: only its uploader can read it.
   *
   * A picture is downsized first, for the reason every other upload here is; a PDF is sent as
   * delivered. A refusal (too large, the unit's allowance spent, a type the server does not take)
   * arrives as `{ problem, message }`, and the message is what the form shows.
   */
  uploadDocument: async (unitId: string, file: File): Promise<UploadedDocument> => {
    const form = new FormData();

    if (file.type.startsWith('image/')) {
      const { blob } = await downsizeImage(file);
      form.append('file', blob, file.name);
    } else {
      form.append('file', file, file.name);
    }

    // No Content-Type header: the browser sets it, and it has to carry the multipart boundary it
    // generated. Setting it by hand produces a body the server cannot parse.
    const response = await fetch(`${API_BASE_URL}/units/${unitId}/announcements/documents`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });

    if (!response.ok) {
      const problem = await response.json().catch(() => null);

      throw new ApiError(
        response.status,
        problem?.message ?? problem?.detail ?? problem?.title ?? 'That file could not be attached.',
      );
    }

    return (await response.json()) as UploadedDocument;
  },

  /**
   * A correction. `attachments` omitted or null leaves them as they are; `{ id }` keeps an existing
   * one at that position, and any live one not listed is detached (kept, never deleted). No screen
   * calls this with attachments yet — there is no correction screen in the web app.
   */
  revise: (
    unitId: string,
    announcementId: string,
    input: {
      title: string;
      body: string;
      attachments?: (AttachmentInput | { id: string })[] | null;
    },
  ) =>
    call<void>(`/units/${unitId}/announcements/${announcementId}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  setPinned: (unitId: string, announcementId: string, isPinned: boolean) =>
    call<void>(`/units/${unitId}/announcements/${announcementId}/pin`, {
      method: 'POST',
      body: JSON.stringify({ isPinned }),
    }),
};
